import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

const PartnerSlideshow = () => {
  const { t, i18n } = useTranslation();
  const { lng } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const partnerName = searchParams.get('partnerName');
  const partnerEmail = searchParams.get('partnerEmail');
  const partnerPhone = searchParams.get('partnerPhone');

  // Defines the order of slides.
  const slides = [
    '/partners/page_1.html',
    '/partners/page_2.html',
    '/partners/page_3.html',
    '/partners/page_4.html',
    '/partners/page_5.html',
    '/partners/page_6.html',
    '/partners/page_7.html',
    '/partners/page_8.html',
    '/partners/page_9.html',
    '/partners/page_10.html',
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const goToPrevious = useCallback(() => {
    const isFirstSlide = currentIndex === 0;
    const newIndex = isFirstSlide ? slides.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
  }, [currentIndex, slides.length]);

  const goToNext = useCallback(() => {
    const isLastSlide = currentIndex === slides.length - 1;
    const newIndex = isLastSlide ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
  }, [currentIndex, slides.length]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowRight') {
        goToNext();
      } else if (event.key === 'ArrowLeft') {
        goToPrevious();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [goToNext, goToPrevious]);

  const getSlideSrc = (index, name, email, phone) => {
    let src = slides[index];
    if (index === 9) { // page_10.html is at index 9
      const params = new URLSearchParams();
      if (name) params.append('partnerName', name);
      if (email) params.append('partnerEmail', email);
      if (phone) params.append('partnerPhone', phone);
      if (params.toString()) {
        src += '?' + params.toString();
      }
    }
    return src;
  };
  
  const mobileSlides = slides
    .filter((_, index) => index !== slides.length - 1)
    .map((slidePath) => {
      const baseName = slidePath.split('/').pop()?.replace('.html', '') ?? '';
      return `/partners_mobile/screenshots/${baseName}.png`;
    });

  if (isMobile) {
    return (
      <div className="min-h-screen bg-gray-950 text-white">
        <div className="mx-auto w-full max-w-4xl px-4 md:px-8 py-8 space-y-6">
          {mobileSlides.map((src) => (
            <figure
              key={src}
              className="rounded-3xl overflow-hidden border border-white/10 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.6)] max-w-2xl mx-auto"
            >
              <img src={src} alt="" className="w-full h-auto block" loading="lazy" />
            </figure>
          ))}

          {(partnerName || partnerEmail || partnerPhone) && (
            <div className="rounded-3xl border border-purple-500/30 bg-gradient-to-br from-purple-950 via-gray-900 to-black p-6 shadow-[0_35px_60px_-15px_rgba(99,102,241,0.45)] max-w-2xl mx-auto">
              {partnerName && <p className="text-xl font-semibold text-white">{partnerName}</p>}
              {partnerEmail && (
                <p className="text-sm text-gray-300 mt-2">
                  <a href={`mailto:${partnerEmail}`} className="underline decoration-purple-400">
                    {partnerEmail}
                  </a>
                </p>
              )}
              {partnerPhone && (
                <p className="text-sm text-gray-300 mt-1">
                  <a href={`tel:${partnerPhone}`} className="underline decoration-purple-400">
                    {partnerPhone}
                  </a>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-screen h-screen bg-gray-900 text-white flex flex-col items-center justify-center">
      <div className="w-full h-full max-w-6xl max-h-[80vh] aspect-video bg-black rounded-lg shadow-2xl overflow-hidden">
        <iframe
          key={currentIndex}
          src={getSlideSrc(currentIndex, partnerName, partnerEmail, partnerPhone)}
          title={`Slide ${currentIndex + 1}`}
          className="w-full h-full border-0"
        />
      </div>
    </div>
  );
};

export default PartnerSlideshow;
